import { AppointmentStatusCategory } from '../../models/access-log-detail';

/**
 * Modifier key for the shared `.badge--*` classes (see `styles/partials/_badges.scss`), chosen for
 * the appointment detail screen's three status chips.
 *
 * The screen does not own a palette: it borrows the existing modifiers whose colours already mean
 * the right thing — `in-review` is the blue in-flight treatment, `approved` the green terminal one,
 * `unknown` the neutral grey. Adding an appointment-only palette would be the parallel-tokens
 * mistake the styling guardrails call out.
 */
export type AppointmentStatusBadgeKind = 'in-review' | 'approved' | 'unknown';

/**
 * Chip colour per status category. Cancelled deliberately shares the neutral grey rather than the
 * red `denied` treatment: on this screen the chip means "this appointment did not happen" — it
 * also covers no-show, rescheduled-away and discontinued — not "someone was denied something", and
 * red on an audit table reads as a finding.
 */
const BADGE_KINDS: Record<AppointmentStatusCategory, AppointmentStatusBadgeKind> = {
  Scheduled: 'in-review',
  Completed: 'approved',
  Cancelled: 'unknown',
};

/**
 * Resolve the chip modifier for a disclosed appointment's status category.
 *
 * Returns `null` when the API sent no category, which is a legal state and not an error: the
 * native status lookup has unassigned ids, and the API deliberately declines to guess a grouping
 * for a status its mapping doesn't recognise. Callers must render the exact `statusType` label
 * with no chip in that case — the fact is never lost, only its colour.
 *
 * A category outside the three the API documents also returns `null` rather than a default chip,
 * so a new server-side grouping shows up as an uncoloured label instead of quietly borrowing the
 * wrong colour on a PHI audit surface.
 */
export function getAppointmentStatusBadgeKind(
  statusCategory: AppointmentStatusCategory | null,
): AppointmentStatusBadgeKind | null {
  if (!statusCategory) {
    return null;
  }
  return BADGE_KINDS[statusCategory] ?? null;
}
